import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box, Typography, Button, Grid, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, TextField,
  Tooltip, Chip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import LockIcon from "@mui/icons-material/Lock";

import {
  fetchBusinessProcess,
  deleteBusinessProcess,
  fetchBusinessProcesses,
  addValueChainProcess,
  removeValueChainProcess,
  updateBusinessProcess,
} from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import DetailSection from "../../components/common/DetailSection";
import DetailField from "../../components/common/DetailField";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import DraggablePaper from "../../components/common/DraggablePaper";
import KpiDetailDialog from "../../components/common/KpiDetailDialog";
import KpiConstraintAlert from "../../components/common/KpiConstraintAlert";
import { useKpiUsageStatus } from "../../hooks/useKpiUsageStatus";

const PROCESS_TYPE_LABELS = {
  CoreProcess: "businessProcess.type.coreProcess",
  BusinessSupportProcess: "businessProcess.type.businessSupportProcess",
  EnterpriseSupportProcess: "businessProcess.type.enterpriseSupportProcess",
};

const PROCESS_CLASS_LABELS = {
  Operation: "businessProcess.class.operation",
  CustomerRelationshipManagement: "businessProcess.class.crm",
  ProductLifeCycleManagement: "businessProcess.class.plm",
  HumanResourceManagement: "businessProcess.class.hrm",
  InvestorRelationshipManagement: "businessProcess.class.irm",
  PartnerRelationshipManagement: "businessProcess.class.prm",
  DataManagement: "businessProcess.class.dm",
  ItServiceManagement: "businessProcess.class.itsm",
  AssetManagement: "businessProcess.class.am",
  Financing: "businessProcess.class.financing",
  GeneralManagement: "businessProcess.class.generalManagement",
};

const EMPTY_KPI = { indicatorName: "", unit: "", measuringMethod: "", evaluationStandard: "", description: "" };

export default function ValueChainDetailPage() {
  const { businessDomainId, valueChainId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [data, setData] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // ── Associated Process state ──
  const [availableProcesses, setAvailableProcesses] = useState([]);
  const [processDialogOpen, setProcessDialogOpen] = useState(false);
  const [selectedProcessId, setSelectedProcessId] = useState("");
  const [processConfirmOpen, setProcessConfirmOpen] = useState(false);
  const [processDeleteTarget, setProcessDeleteTarget] = useState(null);

  // ── KPI state ──
  const [kpiDialogOpen, setKpiDialogOpen] = useState(false);
  const [kpiEditIndex, setKpiEditIndex] = useState(null); // null = add, number = edit
  const [kpiForm, setKpiForm] = useState(EMPTY_KPI);
  const [kpiDeleteIndex, setKpiDeleteIndex] = useState(null);
  const [kpiDeleteOpen, setKpiDeleteOpen] = useState(false);
  const [kpiDetailOpen, setKpiDetailOpen] = useState(false);
  const [kpiDetailRow, setKpiDetailRow] = useState(null);

  // ── KPI 使用状況 ──
  const { usedKpiNames } = useKpiUsageStatus(businessDomainId);

  // ── Delete Value Chain ──
  const handleDelete = async () => {
    try {
      await deleteBusinessProcess(valueChainId);
      notify.success(t("message.delete.success"));
      navigate(`/design/${businessDomainId}/value-chains`);
    } catch (e) { notify.apiError(e); }
    setConfirmOpen(false);
  };

  // ── Loader ──
  const loadData = useCallback(async () => {
    try { setData(await fetchBusinessProcess(valueChainId)); }
    catch (e) { notify.apiError(e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueChainId]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    (async () => {
      try { setAvailableProcesses(await fetchBusinessProcesses(businessDomainId)); }
      catch (e) { /* ignore */ }
    })();
  }, [businessDomainId]);

  // ── Associated Process CRUD ──
  const openProcessAdd = () => {
    setSelectedProcessId("");
    setProcessDialogOpen(true);
  };

  const handleProcessAdd = async () => {
    if (!selectedProcessId) { notify.error(t("message.error.required")); return; }
    try {
      await addValueChainProcess(valueChainId, selectedProcessId);
      notify.success(t("message.create.success"));
      setProcessDialogOpen(false);
      loadData();
    } catch (e) { notify.apiError(e); }
  };

  const handleProcessDelete = async () => {
    try {
      await removeValueChainProcess(valueChainId, processDeleteTarget);
      notify.success(t("message.delete.success"));
      loadData();
    } catch (e) { notify.apiError(e); }
    setProcessConfirmOpen(false);
    setProcessDeleteTarget(null);
  };

  // ── KPI CRUD ──
  const openKpiAdd = () => {
    setKpiEditIndex(null);
    setKpiForm(EMPTY_KPI);
    setKpiDialogOpen(true);
  };

  const openKpiEdit = (index) => {
    const ind = (data.indicators || [])[index];
    setKpiEditIndex(index);
    setKpiForm({
      indicatorName: ind.indicatorName || "",
      unit: ind.unit || "",
      measuringMethod: ind.measuringMethod || "",
      evaluationStandard: ind.evaluationStandard || "",
      description: ind.description || "",
    });
    setKpiDialogOpen(true);
  };

  const handleKpiSave = async () => {
    if (!kpiForm.indicatorName.trim() || !kpiForm.unit.trim()) {
      notify.error(t("message.error.required"));
      return;
    }
    const indicators = [...(data.indicators || [])];
    // 重複チェック（編集時は自分自身を除く）
    const duplicate = indicators.some((ind, i) =>
      ind.indicatorName === kpiForm.indicatorName.trim() && i !== kpiEditIndex
    );
    if (duplicate) {
      notify.error(t("businessProcessKpi.duplicate"));
      return;
    }
    const newIndicator = {
      ...kpiForm,
      indicatorName: kpiForm.indicatorName.trim(),
      unit: kpiForm.unit.trim(),
      createdAt: kpiEditIndex !== null ? indicators[kpiEditIndex].createdAt : new Date(),
    };
    if (kpiEditIndex !== null) {
      indicators[kpiEditIndex] = newIndicator;
    } else {
      indicators.push(newIndicator);
    }
    try {
      await updateBusinessProcess({ ...data, indicators });
      notify.success(kpiEditIndex !== null ? t("message.update.success") : t("message.create.success"));
      setKpiDialogOpen(false);
      loadData();
    } catch (e) { notify.apiError(e); }
  };

  const openKpiDelete = (index) => {
    setKpiDeleteIndex(index);
    setKpiDeleteOpen(true);
  };

  const handleKpiDelete = async () => {
    const indicators = (data.indicators || []).filter((_, i) => i !== kpiDeleteIndex);
    try {
      await updateBusinessProcess({ ...data, indicators });
      notify.success(t("message.delete.success"));
      loadData();
    } catch (e) { notify.apiError(e); }
    setKpiDeleteOpen(false);
    setKpiDeleteIndex(null);
  };

  // availableProcesses からIDキーのマップを作成し、最新の名前・説明を取得する
  const processMap = useMemo(
    () => Object.fromEntries(availableProcesses.map(p => [p.businessProcessId, p])),
    [availableProcesses]
  );

  if (!data) return null;

  const associatedProcesses = data.associatedBusinessProcesses || [];
  const indicators = data.indicators || [];

  const processTypeLabel = PROCESS_TYPE_LABELS[data.businessProcessType]
    ? t(PROCESS_TYPE_LABELS[data.businessProcessType])
    : data.businessProcessType || "";
  const processClassLabel = PROCESS_CLASS_LABELS[data.businessProcessClass]
    ? t(PROCESS_CLASS_LABELS[data.businessProcessClass])
    : data.businessProcessClass || "";

  const hasLockedKpi = indicators.some((ind) => usedKpiNames.has(ind.indicatorName));

  const associatedIds = new Set(associatedProcesses.map((p) => p.businessProcessId));

  const processColumns = [
    {
      field: "businessProcessName", headerName: t("valueChain.process.name"), flex: 1, minWidth: 200,
      renderCell: (params) => {
        const latest = processMap[params.row.businessProcessId];
        return latest?.businessProcessName ?? params.value;
      },
    },
    {
      field: "businessProcessDescription", headerName: t("businessProcess.description"), flex: 1, minWidth: 200,
      renderCell: (params) => {
        const latest = processMap[params.row.businessProcessId];
        return latest?.businessProcessDescription ?? "";
      },
    },
    {
      field: "businessProcessClass", headerName: t("valueChain.process.class"), width: 200,
      valueFormatter: (value) => {
        const key = PROCESS_CLASS_LABELS[value];
        return key ? intl.formatMessage({ id: key }) : (value || "");
      },
    },
    {
      field: "actions", headerName: t("common.actions"), width: 80, sortable: false,
      renderCell: (p) => (
        <IconButton size="small" color="error" onClick={(e) => {
          e.stopPropagation();
          setProcessDeleteTarget(p.row.businessProcessId);
          setProcessConfirmOpen(true);
        }}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  const kpiColumns = [
    {
      field: "indicatorName",
      headerName: t("businessProcessKpi.indicatorName"),
      flex: 1,
      minWidth: 200,
      renderCell: (p) => {
        const isLocked = usedKpiNames.has(p.row.indicatorName);
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {p.row.indicatorName}
            {isLocked && <Chip icon={<LockIcon fontSize="small" />} label={t("common.inUse")} size="small" color="warning" variant="outlined" />}
          </Box>
        );
      },
    },
    { field: "unit", headerName: t("businessProcessKpi.unit"), width: 120 },
    {
      field: "actions", headerName: t("common.actions"), width: 100, sortable: false,
      renderCell: (p) => {
        const isLocked = usedKpiNames.has(p.row.indicatorName);
        return (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Tooltip title={isLocked ? t("kpi.constraint.list.info") : ""}>
              <span>
                <IconButton size="small" color="primary" disabled={isLocked} onClick={(e) => {
                  e.stopPropagation();
                  openKpiEdit(p.row.__index);
                }}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={isLocked ? t("kpi.constraint.list.info") : ""}>
              <span>
                <IconButton size="small" color="error" disabled={isLocked} onClick={(e) => {
                  e.stopPropagation();
                  openKpiDelete(p.row.__index);
                }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  const kpiRows = indicators.map((ind, i) => ({ ...ind, id: i, __index: i }));

  return (
    <Box>
      {hasLockedKpi && <KpiConstraintAlert variant="type-locked" />}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t("valueChain.detail.title")}</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/design/${businessDomainId}/value-chains/${valueChainId}/edit`)}
          >
            {t("common.edit")}
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setConfirmOpen(true)}
          >
            {t("common.delete")}
          </Button>
        </Box>
      </Box>

      <DetailSection>
        <Grid container spacing={2}>
          <Grid size={12} md={6}><DetailField label={t("valueChain.name")} value={data.businessProcessName} /></Grid>
          <Grid size={12} md={3}><DetailField label={t("businessProcess.type")} value={processTypeLabel} /></Grid>
          <Grid size={12} md={3}><DetailField label={t("businessProcess.class")} value={processClassLabel} /></Grid>
          <Grid size={12}><DetailField label={t("valueChain.description")} value={data.businessProcessDescription} /></Grid>
          <Grid size={12}><DetailField label={t("common.url")} value={data.url} /></Grid>
        </Grid>
      </DetailSection>

      {/* KPI（指標）一覧 */}
      <Box sx={{ mt: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography variant="subtitle1">{t("businessProcessKpi.list.title")}</Typography>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openKpiAdd}>
            {t("common.add")}
          </Button>
        </Box>
        <DataGrid
          rows={kpiRows}
          columns={kpiColumns}
          autoHeight
          pageSizeOptions={[5, 10]}
          initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
          disableRowSelectionOnClick
          onRowClick={(params) => { setKpiDetailRow(params.row); setKpiDetailOpen(true); }}
          sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
        />
      </Box>

      {/* Associated Processes */}
      <Box sx={{ mt: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography variant="subtitle1">{t("valueChain.process.list.title")}</Typography>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openProcessAdd}>
            {t("common.add")}
          </Button>
        </Box>
        <DataGrid
          rows={associatedProcesses}
          columns={processColumns}
          getRowId={(r) => r.businessProcessId}
          key={associatedProcesses.map((p) => p.businessProcessId).join(",")}
          autoHeight
          pageSizeOptions={[5, 10]}
          initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
          disableRowSelectionOnClick
        />
      </Box>

      <Box sx={{ mt: 3 }}>
        <Button variant="outlined" onClick={() => navigate(`/design/${businessDomainId}/value-chains`)}>{t("common.back")}</Button>
      </Box>

      {/* Confirm Dialogs */}
      <ConfirmDialog open={confirmOpen} onConfirm={handleDelete} onCancel={() => setConfirmOpen(false)} />
      <ConfirmDialog open={processConfirmOpen} onConfirm={handleProcessDelete} onCancel={() => { setProcessConfirmOpen(false); setProcessDeleteTarget(null); }} />
      <ConfirmDialog open={kpiDeleteOpen} onConfirm={handleKpiDelete} onCancel={() => { setKpiDeleteOpen(false); setKpiDeleteIndex(null); }} />

      {/* Process Add Dialog */}
      <Dialog open={processDialogOpen} onClose={() => setProcessDialogOpen(false)} maxWidth="sm" fullWidth PaperComponent={DraggablePaper}>
        <DialogTitle data-drag-handle sx={{ cursor: "move", userSelect: "none" }}>{t("valueChain.process.add.title")}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <FormControl fullWidth required>
              <InputLabel>{t("valueChain.process.name")}</InputLabel>
              <Select
                value={selectedProcessId}
                onChange={(e) => setSelectedProcessId(e.target.value)}
                label={t("valueChain.process.name")}
              >
                {availableProcesses
                  .filter((p) => !associatedIds.has(p.businessProcessId))
                  .map((p) => (
                    <MenuItem key={p.businessProcessId} value={p.businessProcessId}>
                      {p.businessProcessName}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProcessDialogOpen(false)}>{t("common.cancel")}</Button>
          <Button variant="contained" onClick={handleProcessAdd}>{t("common.add")}</Button>
        </DialogActions>
      </Dialog>

      {/* KPI Detail Dialog */}
      <KpiDetailDialog
        open={kpiDetailOpen}
        onClose={() => { setKpiDetailOpen(false); setKpiDetailRow(null); }}
        indicator={kpiDetailRow}
      />

      {/* KPI Add/Edit Dialog */}
      <Dialog open={kpiDialogOpen} onClose={() => setKpiDialogOpen(false)} maxWidth="sm" fullWidth PaperComponent={DraggablePaper}>
        <DialogTitle data-drag-handle sx={{ cursor: "move", userSelect: "none" }}>
          {kpiEditIndex !== null ? t("businessProcessKpi.edit.title") : t("businessProcessKpi.create.title")}
        </DialogTitle>
        <DialogContent>
          <KpiConstraintAlert
            variant={kpiEditIndex !== null
              ? (usedKpiNames.has(kpiForm.indicatorName) ? "edit-locked" : "edit-unused")
              : "create"}
            sx={{ mt: 1 }}
          />
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              fullWidth required
              label={t("businessProcessKpi.indicatorName")}
              value={kpiForm.indicatorName}
              onChange={(e) => setKpiForm((f) => ({ ...f, indicatorName: e.target.value }))}
            />
            <TextField
              fullWidth required
              label={t("businessProcessKpi.unit")}
              value={kpiForm.unit}
              onChange={(e) => setKpiForm((f) => ({ ...f, unit: e.target.value }))}
            />
            <TextField
              fullWidth multiline rows={4}
              label={t("businessProcessKpi.measuringMethod")}
              value={kpiForm.measuringMethod}
              onChange={(e) => setKpiForm((f) => ({ ...f, measuringMethod: e.target.value }))}
            />
            <TextField
              fullWidth multiline rows={2}
              label={t("businessProcessKpi.evaluationStandard")}
              value={kpiForm.evaluationStandard}
              onChange={(e) => setKpiForm((f) => ({ ...f, evaluationStandard: e.target.value }))}
            />
            <TextField
              fullWidth multiline rows={2}
              label={t("businessProcessKpi.description")}
              value={kpiForm.description}
              onChange={(e) => setKpiForm((f) => ({ ...f, description: e.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKpiDialogOpen(false)}>{t("common.cancel")}</Button>
          <Button
            variant="contained"
            onClick={handleKpiSave}
            disabled={!kpiForm.indicatorName.trim() || !kpiForm.unit.trim() || (kpiEditIndex !== null && usedKpiNames.has(kpiForm.indicatorName))}
          >
            {kpiEditIndex !== null ? t("common.save") : t("common.add")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
